import {Header} from '@/components/commons/header/Header'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {z} from 'zod'
import {ObjectEntries, raise} from '@/utilities/helpers'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledExperienceViewOnly, StyledExperienceViewOnlyContent} from './style'
import {StepsNavigation} from '@/features/experience-view-only/components/steps-navigation/StepsNavigation'
import {ExperienceStep} from '@/features/experience-view-only/components/experience-step/ExperienceStep'
import {BackgroundStep} from '@/features/experience-view-only/components/background-step/BackgroundStep'
import {MerchandisingStep} from '@/features/experience-view-only/components/merchandising-step/MerchandisingStep'
import {LocationStep} from '@/features/experience-view-only/components/location-step/LocationStep'
import {PhotosStep} from '@/features/experience-view-only/components/photos-step/PhotosStep'
import {PricingDiscountStep} from '@/features/experience-view-only/components/pricing-discount-step/PricingDiscountStep'
import {LegalStep} from '@/features/experience-view-only/components/legal-step/LegalStep'
import {useEffect, useMemo, useRef, useState} from 'react'
import {ExperienceStepKeyName} from '@/features/experience-view-only/types'
import {useGetExperience} from '@/features/experience-shell/queries/useGetExperience'
import {remapExperienceForViewOnly} from '@/features/experience-view-only/utils'
import {debounce} from '@utilities/helpers.ts'

export const ExperienceViewOnly = () => {
    const {t} = useTranslation()
    const params = useURLParsedParams(z.object({id: z.coerce.number().int().positive()}))
    const experienceQuery = useGetExperience(params.success ? params.data.id : raise('id is nullish'))
    const experienceRef = useRef<HTMLDivElement>(null)
    const backgroundRef = useRef<HTMLDivElement>(null)
    const merchandisingRef = useRef<HTMLDivElement>(null)
    const locationRef = useRef<HTMLDivElement>(null)
    const photosRef = useRef<HTMLDivElement>(null)
    const pricingDiscountRef = useRef<HTMLDivElement>(null)
    const legalRef = useRef<HTMLDivElement>(null)
    const isAutomaticScroll = useRef(false)

    const [currentStep, setCurrentStep] = useState<ExperienceStepKeyName>(ExperienceStepKeyName.enum.experience)

    const remappedData = experienceQuery.data && remapExperienceForViewOnly(experienceQuery.data)
    const stepsRefMap = useMemo(
        () => ({
            [ExperienceStepKeyName.enum.experience]: experienceRef,
            [ExperienceStepKeyName.enum.background]: backgroundRef,
            [ExperienceStepKeyName.enum.merchandising]: merchandisingRef,
            [ExperienceStepKeyName.enum.location]: locationRef,
            [ExperienceStepKeyName.enum.photos]: photosRef,
            [ExperienceStepKeyName.enum.pricing_discounts]: pricingDiscountRef,
            [ExperienceStepKeyName.enum.legal]: legalRef
        }),
        [
            experienceRef?.current,
            backgroundRef?.current,
            merchandisingRef?.current,
            locationRef?.current,
            photosRef?.current,
            pricingDiscountRef?.current,
            legalRef?.current
        ]
    )
    const onRemoveAutomaticScroll = debounce(() => (isAutomaticScroll.current = false), 700)
    const scrollToSection = (step: ExperienceStepKeyName) => {
        const sectionRef = stepsRefMap[step]
        if (sectionRef?.current) {
            isAutomaticScroll.current = true
            setCurrentStep(step)
            sectionRef.current.scrollIntoView({behavior: 'smooth'})
            onRemoveAutomaticScroll()
        }
    }

    const intersectionObserver = useMemo(
        () =>
            new IntersectionObserver(
                entries => {
                    const intersectingEntry = entries.find(entry => entry.isIntersecting)
                    if (intersectingEntry) {
                        ObjectEntries(stepsRefMap).forEach(([stepKey, ref]) => {
                            if (ref.current === intersectingEntry.target && !isAutomaticScroll.current) {
                                setCurrentStep(stepKey)
                            }
                        })
                    }
                },
                {threshold: 0.2}
            ),
        [
            experienceRef?.current,
            backgroundRef?.current,
            merchandisingRef?.current,
            locationRef?.current,
            photosRef?.current,
            pricingDiscountRef?.current,
            legalRef?.current
        ]
    )

    useEffect(() => {
        Object.values(stepsRefMap).forEach(ref => {
            if (ref.current) {
                intersectionObserver.observe(ref.current)
            }
        })

        return () => {
            Object.values(stepsRefMap).forEach(ref => {
                if (ref.current) {
                    intersectionObserver.unobserve(ref.current)
                }
            })
        }
    }, [
        intersectionObserver,
        experienceRef?.current,
        backgroundRef?.current,
        merchandisingRef?.current,
        locationRef?.current,
        photosRef?.current,
        pricingDiscountRef?.current,
        legalRef?.current,
        experienceQuery.isPending,
        remappedData
    ])

    if (experienceQuery.isPending || !remappedData) {
        return (
            <Flexbox justify="center" align="center" fullWidth style={{height: '100%'}}>
                <Spinner size={32} />
            </Flexbox>
        )
    }

    if (experienceQuery.isError) {
        return <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
    }

    return (
        <StyledExperienceViewOnly>
            <Header />

            <StepsNavigation currentStep={currentStep} onStepClick={scrollToSection} />
            <StyledExperienceViewOnlyContent direction="column" gap={8}>
                <ExperienceStep experience={remappedData} ref={experienceRef} />
                <BackgroundStep experience={remappedData} ref={backgroundRef} />
                <MerchandisingStep experience={remappedData} ref={merchandisingRef} />
                <LocationStep experience={remappedData} ref={locationRef} />
                <PhotosStep experience={remappedData} ref={photosRef} />
                <PricingDiscountStep experience={remappedData} ref={pricingDiscountRef} />
                <LegalStep experience={remappedData} ref={legalRef} />
            </StyledExperienceViewOnlyContent>
        </StyledExperienceViewOnly>
    )
}
